import {Component, EventEmitter, Input, LOCALE_ID, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import { AlertService, IAlertType, PersonsStoreService, StaffStoreService } from '../../../_shared/services';
import { ReactiveFormsModule } from '@angular/forms';
import {
  ClrDatagridModule,
  ClrDatagridSortOrder,
  ClrDatepickerModule, ClrIconModule,
  ClrSelectModule,
  ClrTextareaModule
} from '@clr/angular';
import { CommentsStoreService } from "../../../_shared/services/comments-store.service";
import { CombinedCommentDetail, Personen } from "../../../_shared/interfaces";
import { CommentAddComponent } from "../comment-add/comment-add.component";
import moment from "moment";

import {DatePipe, JsonPipe, NgIf} from "@angular/common";
import { TranslocoDirective } from "@jsverse/transloco";
import { RouterLink } from "@angular/router";
import {
  circleIcon,
  ClarityIcons,
  dotCircleIcon,
  exclamationCircleIcon,
  exclamationTriangleIcon,
  trashIcon
} from "@cds/core/icon";

import { environment as env } from "../../../../environments/environment";
import { CommentTabIndex } from "../comment-feature-card/comment-feature-card.component";

@Component({
  selector: 'app-comment-list',
  standalone: true,
  imports: [
    NgIf,
    ClrDatagridModule,
    TranslocoDirective,
    DatePipe,
    RouterLink,
    ClrTextareaModule,
    ClrSelectModule,
    ClrDatepickerModule,
    ReactiveFormsModule,
    ClrIconModule,
    CommentAddComponent,
    JsonPipe
  ],
  providers: [{ provide: LOCALE_ID, useValue: 'de' }],
  templateUrl: './comment-list.component.html',
  styleUrl: './comment-list.component.sass'
})
export class CommentListComponent implements OnInit, OnChanges {
  persons: Personen[] = [];
  descSort = ClrDatagridSortOrder.DESC;
  loading: boolean = true;
  devMode: boolean;
  combinedCommentsDetails: CombinedCommentDetail[] = [];
  showAddForm = false;

  @Input() commentType: number = 0;
  @Input() companyId: number = 0;
  @Input() projectId: string = '';
  @Input() subId: number = 0;
  @Input() activeTab: number = 0;
  @Input() staffId: number = 0;
  @Input() status: boolean = false;
  @Input() trash: boolean = false;
  // Changed from void to number to pass the tab index
  @Output() activateParentTab = new EventEmitter<number>();

  constructor(
      private commentss: CommentsStoreService,
      private persss: PersonsStoreService,
      private stss: StaffStoreService,
      private alertDispatcher: AlertService,
  ) {
    this.devMode = env.stages.dev;
    ClarityIcons.addIcons(dotCircleIcon, circleIcon, exclamationCircleIcon, exclamationTriangleIcon, trashIcon);
  }

  async ngOnInit() {
    this.loading = true;
    console.log('activateParentTab:', this.activateParentTab);
    this.loadCustomerList(this.companyId);
    await this.loadAllData(this.commentType, this.projectId, this.subId, this.status);
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['status']) {
      await this.loadAllData(this.commentType, this.projectId, this.subId, this.status);
    }
  }

  loadAllData(commentType: number, projectId: string, subId: number, done: boolean) {
    this.loading = true;
    return new Promise<void>((resolve) => {
      this.commentss.getComments(commentType, projectId, subId, done).subscribe({
        next: commentsData => {
          if (!commentsData || !commentsData.object || commentsData.object.length === 0) {
            this.alertDispatcher.show({
              message: ' Keine Kommentare gefunden! ',
              clrAlertType: IAlertType.WARNING
            }, 5000);
            this.loading = false;
            resolve();
            return;
          }

          this.combinedCommentsDetails = commentsData.object.map(comment => ({
            ...comment,
            personName: '',  // Standardwert, wird später aktualisiert
            staffName: ''    // Standardwert, wird später aktualisiert
          }));

          // Counter for tracking when all person and staff data is loaded
          let pendingRequests = this.combinedCommentsDetails.length * 2;

          if (pendingRequests === 0) {
            this.loading = false;
            resolve();
            return;
          }

          // Für jeden Kommentar die zugehörigen Daten laden und aktualisieren
          this.combinedCommentsDetails.forEach(detail => {
            this.persss.getSinglePersons(detail.person_id).subscribe({
              next: personData => {
                if (personData.object.length) {
                  detail.personName = `${personData.object[0].salutation} ${personData.object[0].lastname}`;
                }
                pendingRequests--;
                if (pendingRequests === 0) {
                  this.loading = false;
                  resolve();
                }
              },
              error: (err) => {
                pendingRequests--;
                if (pendingRequests === 0) {
                  this.loading = false;
                  resolve();
                }
              }
            });

            this.stss.getStaffById(detail.staff_id).subscribe({
              next: staffData => {
                if (staffData.object.length) {
                  detail.staffName = `${staffData.object[0].firstname[0]}` + '.' + `${staffData.object[0].lastname}`;
                }
                pendingRequests--;
                if (pendingRequests === 0) {
                  this.loading = false;
                  resolve();
                }
              },
              error: (err) => {
                pendingRequests--;
                if (pendingRequests === 0) {
                  this.loading = false;
                  resolve();
                }
              }
            });
          });
        },
        complete: () => {
        },
        error: (err) => {
          this.loading = false;
          resolve();
        }
      });
    });
  }

  /**
   * Refreshes the comment list and switches to the urgent tab
   * @param commentType The type of comment to load
   * @param projectId The project ID
   * @param subId The sub ID
   * @param done Whether to load done or not done comments
   */
  async refreshListAndSwitchTab(commentType: number, projectId: string, subId: number, done: boolean) {
    await this.loadAllData(commentType, projectId, subId, done);
    this.showAddForm = false;
    // Emit the URGENT tab index (0) to switch to the urgent tab
    this.activateParentTab.emit(CommentTabIndex.URGENT);
  }

  toggleAddForm() {
    this.showAddForm = !this.showAddForm;
  }

  deleteComment(id: number) {
    this.commentss.deleteComment(id)
      .subscribe({
        next: async () => {
          this.trash = false;
          await this.refreshListAndSwitchTab(this.commentType, this.projectId, this.subId, false);
          // Show alert after tab switch and data refresh is complete
          this.alertDispatcher.show({
            message: ' Der Kommentar wurde gelöscht. ',
            clrAlertType: IAlertType.INFO
          });
        },
        error: () => {
          return
        }
      })
  }

  dateDiff(remember: string) {
    moment.locale('de');
    var today = new Date();
    let firstDate = moment(today);
    let secondDate = moment(remember);
    return firstDate.diff(secondDate, 'days');
  }

  /**
   * Updates the done status of a comment and switches to the urgent tab if done=false
   * @param id The comment ID
   * @param done The new done status
   */
  setDone(id: number, done: boolean) {
    this.commentss.updateCommentStatus(id, done)
      .subscribe({
        next: async () => {

          // If a comment is marked as not done (done=false), always switch to the urgent tab
          // This ensures that when a comment is unmarked as done in the "done" tab,
          // the application switches to the "urgent" tab to show the updated list
          if (!done) {
            await this.refreshListAndSwitchTab(this.commentType, this.projectId, this.subId, false);
          } else {
            // If a comment is marked as done, check if we're in the "done" tab (activeTab = 1)
            // If so, switch to the urgent tab to show the updated list
            if (this.activeTab === CommentTabIndex.DONE) {
              await this.refreshListAndSwitchTab(this.commentType, this.projectId, this.subId, false);
            } else {
              // Otherwise, just refresh the current list
              await this.loadAllData(this.commentType, this.projectId, this.subId, this.status);
            }
          }

          // Show alert after tab switch and data refresh is complete
          this.alertDispatcher.show({
            message: ' Der Status von Kommentar ' + id + ' wurde geändert. ',
            clrAlertType: IAlertType.INFO
          }, 5000);
        },
        error: (err) => {
          console.error('setDone: Error updating comment status', err);
          return
        }
      });
  }

  loadCustomerList(companyid: number) {
    this.persss.getPersonInCompany(companyid, true)
      .subscribe({
        next: data => {
          if (!data || !data.object || data.object.length === 0) {
            this.alertDispatcher.show({
              message: ' Keine Person gefunden! ',
              clrAlertType: IAlertType.WARNING
            }, 5000);
            return;
          }
          this.persons = data.object;
        },
        complete: () => {
          // Sortiere die Liste nach dem Nachnamen
          this.persons.sort((a, b) => {
            if (a.lastname < b.lastname) {
              return -1;
            }
            if (a.lastname > b.lastname) {
              return 1;
            }
            return 0;
          });

        },
        error: () => {
          return
        }
      });
  }

  findButtonClass(remember: string) {
    let buttonclass:string;
    switch (true) {
      case (this.dateDiff(remember) > 0):
        buttonclass = "alert alert-danger alert-sm";
        break
      case ((this.dateDiff(remember) >= -5) && (this.dateDiff(remember) <= 0)):
        buttonclass = "alert alert-warning alert-sm";
        break
      default:
        buttonclass = ""
        break
    }

    return buttonclass;
  }

  protected readonly CommentTabIndex = CommentTabIndex;
}
